/**
 * @file dnd.ts
 * @description 全局拖拽工具函数，提供类型安全的拖拽数据管理
 * @created 2026-02-03
 */

/**
 * 拖拽类型定义
 * 可以根据项目需求扩展更多类型
 */
export enum DragType {
  CONFIGURATION = 'application/x-rapoo-configuration',
  KEY_MAPPING = 'application/x-rapoo-key-mapping',
  // DEVICE = 'application/x-rapoo-device', // 示例：未来可能的其他类型
}

/**
 * 基础拖拽数据接口
 */
export interface BaseDragData {
  id?: string
  name?: string
  [key: string]: any
}

/**
 * 设置拖拽数据
 * @param dataTransfer DataTransfer 对象
 * @param type 拖拽类型 (MIME type)
 * @param data 拖拽数据对象
 */
export const setDragData = <T extends BaseDragData>(
  dataTransfer: DataTransfer,
  type: DragType | string,
  data: T,
) => {
  dataTransfer.setData(type, JSON.stringify(data))
  dataTransfer.effectAllowed = 'copy'
}

/**
 * 使用元素当前在视口中的视觉尺寸创建拖拽预览。
 *
 * Chromium 在 draggable 元素位于 transform: scale() 容器中时，默认拖拽快照
 * 可能会使用缩放前的布局尺寸。这里将克隆元素挂到 body，并在独立容器中还原
 * 元素当前的视觉尺寸，避免拖拽预览随页面缩放比例异常放大。
 */
export const setElementDragImage = (event: DragEvent, source: HTMLElement) => {
  const dataTransfer = event.dataTransfer
  if (!dataTransfer) return

  const rect = source.getBoundingClientRect()
  if (!rect.width || !rect.height || !source.offsetWidth || !source.offsetHeight) return

  const preview = document.createElement('div')
  const clone = source.cloneNode(true) as HTMLElement
  const scaleX = rect.width / source.offsetWidth
  const scaleY = rect.height / source.offsetHeight

  Object.assign(preview.style, {
    position: 'fixed',
    left: '-10000px',
    top: '-10000px',
    width: `${rect.width}px`,
    height: `${rect.height}px`,
    overflow: 'hidden',
    pointerEvents: 'none',
  })

  clone.removeAttribute('id')
  clone.setAttribute('aria-hidden', 'true')
  Object.assign(clone.style, {
    width: `${source.offsetWidth}px`,
    height: `${source.offsetHeight}px`,
    margin: '0',
    transform: `scale(${scaleX}, ${scaleY})`,
    transformOrigin: 'top left',
    transition: 'none',
    pointerEvents: 'none',
  })

  preview.appendChild(clone)
  document.body.appendChild(preview)

  const offsetX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)
  const offsetY = Math.min(Math.max(event.clientY - rect.top, 0), rect.height)
  dataTransfer.setDragImage(preview, offsetX, offsetY)

  setTimeout(() => preview.remove(), 0)
}

/**
 * 微拖拽点击兜底。
 *
 * 背景：原生 HTML5 拖拽在 Chromium 中按住移动约 4px 即启动，且启动后本次 click 会被吞掉。
 * 高 DPI 鼠标点击时手部微动极易越过该阈值，导致"点击改键偶发失效、需要点多次"。
 * 该内核阈值不可配置，也无法通过手势中途切换 draggable 绕过
 * （首次越过阈值时元素不可拖会直接终止本次手势的起拖判定）。
 *
 * 方案：拖拽照常启动，在 dragend 时判断——若本次拖拽没有落到任何目标
 * （dropEffect === 'none'）且全程位移小于阈值，则视为一次被误判成拖拽的点击，由调用方补执行点击逻辑。
 *
 * 用法：
 * - dragstart 中调用 onDragStart(e)
 * - 元素绑定 @drag="onDrag"
 * - dragend 中若 shouldTreatAsClick(e) 为 true，执行点击逻辑
 *
 * @param threshold 位移阈值（像素），全程移动小于该值的无目标拖拽视为点击
 */
export const createMicroDragClickFallback = (threshold = 16) => {
  let startX = 0
  let startY = 0
  let maxDistance = Infinity

  // 部分场景下 drag/dragend 事件坐标会异常上报为 (0,0)，跳过这些点
  const isValidPoint = (e: DragEvent) => e.screenX !== 0 || e.screenY !== 0

  const distanceFromStart = (e: DragEvent) => Math.hypot(e.screenX - startX, e.screenY - startY)

  const onDragStart = (e: DragEvent) => {
    startX = e.screenX
    startY = e.screenY
    maxDistance = 0
  }

  const onDrag = (e: DragEvent) => {
    if (!isValidPoint(e)) return
    maxDistance = Math.max(maxDistance, distanceFromStart(e))
  }

  const shouldTreatAsClick = (e: DragEvent): boolean => {
    let max = maxDistance
    maxDistance = Infinity
    if (e.dataTransfer && e.dataTransfer.dropEffect !== 'none') return false
    if (isValidPoint(e)) {
      max = Math.max(max, distanceFromStart(e))
    }
    return max < threshold
  }

  return { onDragStart, onDrag, shouldTreatAsClick }
}

/**
 * 获取拖拽数据
 * @param dataTransfer DataTransfer 对象
 * @param type 拖拽类型 (MIME type)
 * @returns 解析后的数据对象或 null
 */
export const getDragData = <T extends BaseDragData>(
  dataTransfer: DataTransfer,
  type: DragType | string,
): T | null => {
  const data = dataTransfer.getData(type)
  if (!data) return null
  try {
    return JSON.parse(data) as T
  } catch (e) {
    console.error(`Failed to parse drag data for type ${type}`, e)
    return null
  }
}
